import { FileSyncOutlined, LoadingOutlined } from '@ant-design/icons'
import { Dropdown, Menu, Tooltip } from 'antd'
import { useRef, useState } from 'react'
import { fetchClient } from '../../api/typedClient'
import { UploadStatusModal } from './UploadStatusModal'
import { handleUploadResponse } from './utils/uploadStatus'

interface MasterDataUploadDownloadButtonsProps {
  entityType: string
  refreshCallback?: () => void
}

// Mapping from singular to plural forms for API endpoints
const pluralMapping: Record<string, string> = {
  company: 'companies',
  counterparty: 'companies',
  location: 'locations',
  product: 'products',
  terminal: 'terminals',
  site: 'sites',
  tank: 'tanks',
}

export function MasterDataUploadDownloadButtons({
  entityType,
  refreshCallback,
}: MasterDataUploadDownloadButtonsProps) {
  const hiddenFileInput = useRef<HTMLInputElement>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [uploadStatusModalVisible, setUploadStatusModalVisible] =
    useState(false)
  const [statusResponse, setStatusResponse] = useState<unknown>()

  // Get the plural form for the API endpoint
  const apiType = pluralMapping[entityType] || entityType

  const handleClick = (input: React.RefObject<HTMLInputElement>) => {
    input.current?.click()
  }

  const handleExport = async () => {
    setIsLoading(true)
    try {
      // Use the correct endpoint based on entity type
      const endpoint =
        entityType === 'company' || entityType === 'counterparty'
          ? `/crossroads/network/${apiType}/bulk_download`
          : `/crossroads/entity/${apiType}/bulk_download`

      const { data, error } = await fetchClient.POST(
        // @ts-expect-error TODO: Add bulk_download endpoints to OpenAPI spec
        endpoint,
        {
          body: {},
          headers: {
            'X-Tenant-Name': 'Gravitate', // Always use Gravitate for master data
          },
          parseAs: 'blob',
        },
      )

      if (error) {
        throw new Error(`HTTP error: ${JSON.stringify(error)}`)
      }

      const blob = data as unknown as Blob

      // Ensure we have a valid blob with content
      if (blob.size === 0) {
        throw new Error('Downloaded file is empty')
      }

      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', `master_${apiType}.xlsx`)
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
    } catch (error) {
      console.error('Download failed:', error)
      // Show user-friendly error message
      if ((error as Error)?.message === 'Downloaded file is empty') {
        alert(
          'The downloaded file is empty. This might be because there are no records to export.',
        )
      } else {
        alert('Download failed. Please try again.')
      }
    } finally {
      setIsLoading(false)
    }
  }

  const handleUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files.length > 0) {
      setIsLoading(true)
      setUploadStatusModalVisible(true)
      const file = event.target.files[0]
      const formData = new FormData()
      formData.append('file', file)

      try {
        // Use the correct endpoint based on entity type
        const uploadEndpoint =
          entityType === 'company' || entityType === 'counterparty'
            ? `/crossroads/network/${apiType}/bulk_upload`
            : `/crossroads/entity/${apiType}/bulk_upload`

        const { data, error } = await fetchClient.POST(
          // @ts-expect-error TODO: Add bulk_upload endpoints to OpenAPI spec
          uploadEndpoint,
          {
            body: formData,
            headers: {
              'X-Tenant-Name': 'Gravitate', // Always use Gravitate for master data
            },
            bodySerializer: (body: FormData) => body,
          },
        )

        if (error) {
          throw new Error(`Upload failed: ${JSON.stringify(error)}`)
        }

        handleUploadResponse(
          data,
          setIsLoading,
          setStatusResponse,
          setUploadStatusModalVisible,
          null,
          hiddenFileInput,
        )

        if (hiddenFileInput.current) hiddenFileInput.current.value = ''
      } catch (error) {
        console.error('Upload failed:', error)
        setIsLoading(false)
        setUploadStatusModalVisible(false)
        if (hiddenFileInput.current) hiddenFileInput.current.value = ''
      }
    }
  }

  const excelDropdownOptions = [
    {
      title: 'Download Template & Data',
      action: 'download',
    },
    {
      title: 'Upload From Excel',
      action: 'upload',
    },
  ]

  const handleOptionClick = (option: { title: string; action: string }) => {
    if (option.action === 'upload') {
      handleClick(hiddenFileInput)
    } else if (option.action === 'download') {
      handleExport()
    }
  }

  const handleMenuClick = (e: { key: string }) => {
    const optionClicked = excelDropdownOptions[parseInt(e.key)]
    handleOptionClick(optionClicked)
  }

  const menu = (
    <Menu onClick={handleMenuClick}>
      {excelDropdownOptions.map((option, index) => (
        <Menu.Item key={index}>{option.title}</Menu.Item>
      ))}
    </Menu>
  )

  return (
    <>
      <Tooltip title='Work in Excel'>
        <Dropdown.Button
          style={{ width: 'auto' }}
          overlay={menu}
          onClick={() => handleOptionClick(excelDropdownOptions[0])}
          disabled={isLoading}
        >
          {isLoading ? (
            <LoadingOutlined style={{ fontSize: '1.2em' }} />
          ) : (
            <FileSyncOutlined style={{ fontSize: '1.2em' }} />
          )}
        </Dropdown.Button>
      </Tooltip>
      <input
        type='file'
        accept='.xlsx,.xls'
        onChange={handleUpload}
        ref={hiddenFileInput}
        style={{ display: 'none' }}
      />
      <UploadStatusModal
        uploadStatusModalVisible={uploadStatusModalVisible}
        setUploadStatusModalVisible={setUploadStatusModalVisible}
        statusResponse={statusResponse}
        isUploading={isLoading}
        onClose={refreshCallback}
      />
    </>
  )
}
